// src/EchipeTree.js
import * as React from "react";
import { useState, useEffect, useMemo, useCallback } from "react";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { useDataProvider, useNotify, useRefresh, useRedirect, Title } from "react-admin";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import { useCanManage } from "./usePermissions";

const NEASIGNATI_KEY = "neasignati";

// Construieste structura de arbore din echipe (ierarhic) + colaboratori (leaf-uri)
const buildTreeData = (echipe, colaboratori) => {
    const colaboratoriByEchipa = {};
    const neasignati = [];

    colaboratori.forEach((c) => {
        const echipaId = c.echipa ? extractId(c.echipa) : null;
        if (echipaId) {
            if (!colaboratoriByEchipa[echipaId]) colaboratoriByEchipa[echipaId] = [];
            colaboratoriByEchipa[echipaId].push(c);
        } else {
            neasignati.push(c);
        }
    });

    const colaboratorToNode = (c) => ({
        key: `colaborator-${c.id}`,
        title: `${c.nume} ${c.prenume}`,
        isLeaf: true,
        type: "colaborator",
        rawId: c.id,
        echipaId: c.echipa ? extractId(c.echipa) : null,
    });

    const echipaToNode = (e) => ({
        key: `echipa-${e.id}`,
        title: e.nume,
        type: "echipa",
        rawId: e.id,
        children: [
            ...echipe
                .filter((child) => extractId(child.parent) === e.id)
                .map(echipaToNode),
            ...(colaboratoriByEchipa[e.id] || []).map(colaboratorToNode),
        ],
    });

    const rootEchipe = echipe.filter((e) => !e.parent);

    const tree = rootEchipe.map(echipaToNode);

    // Nod virtual pentru colaboratorii neasignati
    tree.push({
        key: NEASIGNATI_KEY,
        title: "Neasignați",
        type: "root-neasignati",
        children: neasignati.map(colaboratorToNode),
    });

    return tree;
};

// Randul vizat de drop -> echipa in care ajunge colaboratorul.
// Drop-ul "intre randuri" e rezolvat la echipa randului de referinta: langa un
// coleg inseamna aceeasi echipa cu el, pe "Neasignati" inseamna fara echipa.
const resolveTargetEchipa = (node) => {
    if (!node) return null;
    if (node.type === "echipa") return node.rawId;
    if (node.type === "colaborator") return node.echipaId ?? null;
    return null;
};

// API Platform poate returna relatiile fie ca IRI string ("/api/echipe/3"), fie ca obiect {id:3,...}
const extractId = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value.id;
    if (typeof value === "string") {
        const match = value.match(/\/(\d+)$/);
        return match ? parseInt(match[1], 10) : null;
    }
    return value;
};

export const EchipeTree = () => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const redirect = useRedirect();
    const canManage = useCanManage(); // admin sau superadmin - doar acestia pot crea/reorganiza echipe

    const [echipe, setEchipe] = useState([]);
    const [colaboratori, setColaboratori] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [echipeRes, colabRes] = await Promise.all([
                dataProvider.getList("echipe", {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: "id", order: "ASC" },
                    filter: {},
                }),
                dataProvider.getList("colaboratori", {
                    pagination: { page: 1, perPage: 1000 },
                    sort: { field: "id", order: "ASC" },
                    filter: {},
                }),
            ]);
            setEchipe(echipeRes.data);
            setColaboratori(colabRes.data);
        } catch (err) {
            notify("Eroare la încărcarea echipelor/colaboratorilor", { type: "error" });
        } finally {
            setLoading(false);
        }
    }, [dataProvider, notify]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const treeData = useMemo(() => buildTreeData(echipe, colaboratori), [echipe, colaboratori]);

    useEffect(() => {
        // expandeaza tot la prima incarcare
        const allKeys = [];
        const collect = (nodes) => {
            nodes.forEach((n) => {
                if (n.children && n.children.length) {
                    allKeys.push(n.key);
                    collect(n.children);
                }
            });
        };
        collect(treeData);
        setExpandedKeys(allKeys);
    }, [echipe.length]);

    const handleDrop = async (info) => {
        // doar admin/superadmin pot reorganiza arborele (mutare colaboratori/echipe)
        if (!canManage) {
            notify("Nu ai dreptul de a reorganiza echipele", { type: "warning" });
            return;
        }

        const dragNode = info.dragNode;
        const dropNode = info.node;

        // Mutarea unei ECHIPE ramane strict "inside": intre randuri ar fi ambiguu
        // in ce parinte ajunge.
        if (dragNode.type === "echipa" && info.dropToGap) {
            notify("Trage direct pe numele echipei (nu între rânduri)", { type: "warning" });
            return;
        }

        try {
            if (dragNode.type === "colaborator") {
                // Acceptam orice rand ca tinta, inclusiv "intre randuri": rc-tree
                // evalueaza pozitia "inside" doar daca mouse-ul s-a deplasat spre
                // dreapta fata de punctul de start (calcDropPosition ->
                // rawDropLevelOffset > -1.5), altfel anuleaza drag-ul complet.
                // Traducem noi randul vizat in echipa destinatie.
                const newEchipaId = resolveTargetEchipa(dropNode);

                await dataProvider.update("colaboratori", {
                    id: dragNode.rawId,
                    data: {
                        echipa: newEchipaId ? `/api/echipe/${newEchipaId}` : null,
                    },
                    previousData: {},
                });

                notify("Colaborator reasignat", { type: "success" });
            } else if (dragNode.type === "echipa") {
                if (dropNode.type === "colaborator") {
                    notify("Nu poți plasa o echipă peste un colaborator", { type: "warning" });
                    return;
                }
                if (dropNode.type === "root-neasignati") {
                    notify("O echipă nu poate fi plasată în Neasignați", { type: "warning" });
                    return;
                }
                if (dropNode.rawId === dragNode.rawId) return;

                const newParentId = dropNode.type === "echipa" ? dropNode.rawId : null;

                await dataProvider.update("echipe", {
                    id: dragNode.rawId,
                    data: {
                        parent: newParentId ? `/api/echipe/${newParentId}` : null,
                    },
                    previousData: {},
                });

                notify("Echipă reorganizată", { type: "success" });
            }

            await loadData();
            refresh();
        } catch (err) {
            notify("Eroare la actualizare: " + (err.message || ""), { type: "error" });
        }
    };

    const allowDrop = ({ dragNode, dropNode, dropPosition }) => {
        // doar admin/superadmin pot drag&drop deloc
        if (!canManage) return false;
        // Echipele se muta doar "inside", direct pe randul altei echipe.
        if (dragNode?.type === "echipa") {
            return dropNode.type !== "colaborator" && dropPosition === 0;
        }
        // Colaboratorii: acceptam orice pozitie. Daca refuzam -1/1, rc-tree
        // considera drop-ul interzis si anuleaza drag-ul cand cursorul nu se
        // deplaseaza spre dreapta - randul echipei devine practic inert.
        return true;
    };

    // Click pe un nod din arbore: colaborator -> editare colaborator,
    // echipa -> editare echipa. "Neasignați" e doar un folder virtual, fara actiune.
    // Editarea efectiva e oricum restrictionata mai departe in colaboratori.js/echipe.js
    // pentru non-admini (formular read-only) - aici doar navigam.
    const handleSelect = (selectedKeys, info) => {
        const node = info.node;
        if (node.type === "colaborator") {
            redirect("edit", "colaboratori", node.rawId);
        } else if (node.type === "echipa") {
            redirect("edit", "echipe", node.rawId);
        }
    };

    if (loading) {
        return (
            <Card style={{ margin: "1em" }}>
                <CardContent>Se încarcă...</CardContent>
            </Card>
        );
    }

    return (
        <Card style={{ margin: "1em" }}>
            <Title title="Echipe și colaboratori" />
            <CardContent>
                {canManage && (
                    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => redirect("/echipe/create")}
                        >
                            Creează echipă
                        </Button>
                    </div>
                )}
                <p style={{ marginBottom: 12, color: "#666" }}>
                    {canManage
                        ? "Click pe un colaborator sau o echipă pentru a edita. Trage un colaborator peste o echipă pentru a-l asocia, sau o echipă peste alta pentru a stabili relația de subechipă. Trage peste „Neasignați” pentru a elimina asocierea."
                        : "Click pe o echipă pentru a o vizualiza. Doar adminii pot crea echipe sau reorganiza colaboratorii."}
                </p>
                <style>{`.rc-tree-node-content-wrapper { cursor: pointer; }`}</style>
                <Tree
                    treeData={treeData}
                    draggable={canManage}
                    expandedKeys={expandedKeys}
                    onExpand={setExpandedKeys}
                    onDrop={handleDrop}
                    allowDrop={allowDrop}
                    onSelect={handleSelect}
                />
            </CardContent>
        </Card>
    );
};
